import { FormikErrors } from 'formik';

import { GitFormModel } from '@/react/portainer/gitops/types';
import { HelmValuesFilesSection } from '@/react/edge/edge-stacks/CreateView/HelmChartForm/HelmValuesFilesSection';
import { HelmInstallOptionsSection } from '@/react/edge/edge-stacks/CreateView/HelmChartForm/HelmInstallOptionsSection';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { TextTip } from '@@/Tip/TextTip';

interface HelmDeploymentFieldsetFormValues {
  helmChartPath?: string;
  helmVersion?: string;
  helmValuesFiles?: string[];
}

interface HelmDeploymentFieldsetProps {
  stackName: string;
  gitModel: GitFormModel;
  values: HelmDeploymentFieldsetFormValues;
  setFieldValue: (field: string, value: unknown) => void;
  errors: FormikErrors<HelmDeploymentFieldsetFormValues>;
  stackId?: number;
  fromEdgeStack?: boolean;
}

export function HelmDeploymentFieldset({
  stackName,
  gitModel,
  values,
  setFieldValue,
  errors,
  stackId,
  fromEdgeStack,
}: HelmDeploymentFieldsetProps) {
  const { helmChartPath } = values;
  const { helmVersion } = values;
  const helmValuesFiles = values.helmValuesFiles || [];
  return (
    <>
      <FormSection title="Deploy to">
        <div className="form-group">
          <label
            htmlFor="helm-namespace"
            className="col-sm-3 col-lg-2 control-label text-left"
          >
            Namespace
          </label>
          <div className="col-sm-9 col-lg-10">
            <input
              id="helm-namespace"
              type="text"
              className="form-control"
              value="(Determined at deployment time)"
              readOnly
              disabled
            />
            <span className="help-block">
              The namespace is configured per edge device and determined during
              deployment
            </span>
          </div>
        </div>

        <div className="form-group">
          <label
            htmlFor="helm-release-name"
            className="col-sm-3 col-lg-2 control-label text-left"
          >
            Release name
          </label>
          <div className="col-sm-9 col-lg-10">
            <input
              id="helm-release-name"
              type="text"
              className="form-control"
              value={`edge-${stackName}`}
              readOnly
              disabled
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Helm chart">
        <TextTip color="blue" className="mb-3">
          Helm chart configuration from your Git repository. The version is
          determined by Chart.yaml.
        </TextTip>

        <FormControl
          label="Helm chart path"
          tooltip={
            helmChartPath
              ? 'Path to the Helm chart folder in the Git repository'
              : 'Using the root folder of the repository'
          }
        >
          <Input
            id="helm-chart-path"
            value={helmChartPath || './'}
            readOnly
            disabled
            data-cy="helm-chart-path-input"
          />
        </FormControl>

        <FormControl
          label="Version"
          tooltip="The version of the Helm chart from Chart.yaml. This cannot be changed when deploying from Git."
        >
          <Input
            id="helm-version"
            value={helmVersion || 'Not available'}
            readOnly
            disabled
            data-cy="helm-version-input"
          />
        </FormControl>

        <div className="form-group">
          <label
            htmlFor="helm-values-files"
            className="col-sm-3 col-lg-2 control-label text-left"
          >
            Values files
          </label>
          <div className="col-sm-9 col-lg-10" id="helm-values-files">
            <HelmValuesFilesSection
              valuesFiles={helmValuesFiles}
              gitModel={gitModel}
              errors={errors}
              onValuesFileChange={(index, value) =>
                setFieldValue(`helmValuesFiles.${index}`, value)
              }
              variant="edit"
              stackID={stackId}
              fromEdgeStack={fromEdgeStack}
            />
          </div>
        </div>
      </FormSection>

      <HelmInstallOptionsSection />
    </>
  );
}
